import { useActivitiesContext } from '../../../context'
import { useMemo } from 'react'
import { ActivityStatus, DEFAULT_ACTIVITY_MINUTES } from '../../../constants'
import { ENTITY_TYPES } from '@sales-center/common'
import { add } from 'date-fns'

/**
 * A create seeded from a calendar click (SH-2133).
 *
 * `start` is null on surfaces with no time axis (month cell, keyboard
 * activation). Those seed the Due *date* only and leave Start empty.
 *
 * The AC reads "Start Date and Due Date are pre-filled ... and I must
 * manually provide a Due Time (and optional Start Time)", but those two
 * clauses cannot both hold in this codebase: useValidations requires a Start
 * Time whenever a Start Date is set (startDate && !startTime -> Required), so
 * seeding the Start date makes the Start time mandatory and the form
 * unsubmittable until the user fills a field the AC calls optional. Seeding
 * Due only leaves exactly one required field — the Due Time — which is the
 * behaviour the AC describes. This is the mob decision (REQ-11); the AC
 * wording needs amending rather than the code.
 */
export type ActivityPrefill = {
  day: Date
  start: Date | null
}

export function useInitValues(
  ownerId: string | null,
  prefill?: ActivityPrefill,
  attachToMode?: boolean,
) {
  const [{ current, activities }] = useActivitiesContext()

  return useMemo(() => {
    const activity = activities.find((activity) => activity.id === current)

    const dueDateDefault = add(new Date(), {
      days: 1,
    }).setHours(8, 0)

    // An edit always wins over a click seed — `activity` means the form was
    // opened on an existing record, never from empty grid space.
    const seed = activity ? undefined : prefill
    const seedStart = seed?.start ?? null
    const seedDay = seed?.day ?? null
    const seedDue = seedStart
      ? add(seedStart, { minutes: DEFAULT_ACTIVITY_MINUTES })
      : seedDay

    // A date-only seed deliberately leaves the Due *time* empty for the user to
    // supply; only a timed click fills both.
    return {
      subject: activity ? activity.subject : '',
      description: activity ? activity.description : '',
      activityTypeId: activity ? activity.activityTypeId : '',
      ownerId: activity
        ? activity.ownerId &&
          activity.ownerId !== '00000000-0000-0000-0000-000000000000'
          ? activity.ownerId
          : 'unassigned'
        : ownerId && ownerId !== '00000000-0000-0000-0000-000000000000'
          ? ownerId
          : 'unassigned',
      startDate: activity?.startDate ? new Date(activity.startDate) : seedStart,
      startTime: activity?.startDate ? new Date(activity.startDate) : seedStart,
      dueDate: activity
        ? new Date(activity.dueDate)
        : (seedDue ?? dueDateDefault),
      dueTime: activity
        ? new Date(activity.dueDate)
        : seedStart
          ? seedDue
          : seed
            ? null
            : dueDateDefault,
      status: activity ? activity.status : ActivityStatus.Todo,
      reminderThreshold: activity ? activity.reminderThreshold : 'None',
      // SH-1432 trap 1: the shared create form hardcodes this `true`, which on the
      // calendar Edit path would silently move the parent's next-action date on
      // every save. There (attach mode with an existing activity loaded) force it
      // OFF; the user must explicitly opt in via the checkbox. Create keeps the
      // `true` default. The entity-scoped Lead/Opportunity Activity panel also
      // reaches this form with an activity loaded (Details -> Edit), but that
      // flow is outside SH-1432, so it keeps its existing `true` default.
      setDueDateAsNextActionDate: activity && attachToMode ? false : true,
      setDueDateAsExpectedClosingDate: false,
      // SH-1428: calendar "Create Event" has no pre-selected entity — the Attach-To
      // picker sets entityId/entityType via setFieldValue on selection, so they are
      // left unseeded in create mode.
      // SH-1432: on the Edit path the activity is already attached, so seed its
      // entityId/entityType here. This lets the update payload round-trip the
      // association and lets the disabled Attach-To render the pre-filled entity.
      // Activity.entityType is PascalCase ('Lead') — exactly the ENTITY_TYPES
      // keys — but prepareData serialises the ENTITY_TYPES *value* ('LEAD') on the
      // wire, matching the create path; map through the enum rather than seeding raw.
      ...(activity
        ? {
            entityType: ENTITY_TYPES[activity.entityType],
            entityId: activity.entityId,
          }
        : {}),
    }
  }, [current, activities, ownerId, prefill, attachToMode])
}
